import React, { useCallback, useEffect, useRef, useState } from "react";

export function SectionJump({ href, locale }) {
  return <a className="qa-section-jump" href={href} aria-label={locale === "de" ? "Zum nächsten Abschnitt" : "Go to next section"}><span className="material-symbols-outlined" aria-hidden="true">keyboard_arrow_down</span></a>;
}

export function CardSection({ id, data }) {
  if (!data?.items?.length) return null;
  return <section id={id} className="content-wrapper qa-case-section"><div className="content-header"><h2 className="h2">{data.title}</h2>{data.intro && <p className="bodycopy qa-section-intro">{data.intro}</p>}</div><div className={`qa-text-grid qa-text-grid--${data.items.length}`}>{data.items.map(([title, text]) => <article className="qa-module-card" key={title}><h3 className="h4">{title}</h3><p className="bodycopy">{text}</p></article>)}</div></section>;
}

export function Overview({ data }) {
  if (!data?.items?.length) return null;
  return <section id="overview" className="content-wrapper qa-case-section qa-overview-section">
    <div className="content-header"><h2 className="h2">{data.title}</h2><p className="bodycopy qa-section-intro">{data.lead}</p></div>
    <div className="qa-overview-grid">{data.items.map(([title, text]) => <article className="qa-module-card" key={title}><h3 className="h4">{title}</h3><p className="bodycopy">{text}</p></article>)}</div>
    <p className="bodycopy qa-overview-disclosure">{data.disclosure}</p>
  </section>;
}

export function CoverageTable({ data, locale }) {
  const fallbackHeadings = locale === "de" ? ["Bereich", "Frage", "Prüfung", "Evidenz"] : ["Area", "Question", "Check", "Evidence"];
  const headings = data.headings || fallbackHeadings;
  return <section id="coverage" className="content-wrapper qa-case-section"><div className="content-header"><h2 id="coverage-heading" className="h2">{data.title}</h2>{data.intro && <p className="bodycopy qa-section-intro">{data.intro}</p>}</div><div className="qa-table-region" role="region" aria-labelledby="coverage-heading" tabIndex="0"><table className="qa-table"><caption className="visually-hidden">{data.caption}</caption><thead><tr>{headings.slice(0, data.rows[0].length).map(heading => <th scope="col" key={heading}>{heading}</th>)}</tr></thead><tbody>{data.rows.map(row => <tr key={row[0]}>{row.map((cell, index) => index === 0 ? <th scope="row" key={cell}>{cell}</th> : <td key={`${row[0]}-${index}`}>{cell}</td>)}</tr>)}</tbody></table></div><SectionJump href="#scenarios" locale={locale} /></section>;
}

export function Scenarios({ data, locale }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const labels = locale === "de" ? { start: "Ausgangslage", steps: "Ablauf", result: "Erwartung", evidence: "Evidenz" } : { start: "Starting point", steps: "Steps", result: "Expected result", evidence: "Evidence" };
  const scenario = data.items[activeIndex];
  const tabId = index => `scenario-tab-${index}`;
  const panelId = "scenario-panel";
  const handleTabKeyDown = (event, index) => {
    let nextIndex = index;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % data.items.length;
    else if (event.key === "ArrowLeft") nextIndex = (index - 1 + data.items.length) % data.items.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = data.items.length - 1;
    else return;
    event.preventDefault();
    setActiveIndex(nextIndex);
    event.currentTarget.parentElement.querySelectorAll('[role="tab"]')[nextIndex].focus();
  };
  const list = (items, ordered = false) => { const List = ordered ? "ol" : "ul"; return <List className="bodycopy">{items.map(item => <li key={item}>{item}</li>)}</List>; };
  const rows = [[labels.start, <p className="bodycopy">{scenario.startingPoint}</p>], [labels.steps, list(scenario.steps, true)], [labels.result, <><div>{list(scenario.outcomes)}</div><p className="bodycopy qa-scenario-evidence"><strong>{labels.evidence}:</strong> {scenario.evidence}</p></>]];
  const tabLabel = (item, index) => item.tab || (locale === "de" ? `Szenario ${String(index + 1).padStart(2, "0")}` : `Scenario ${String(index + 1).padStart(2, "0")}`);
  return <section id="scenarios" className="content-wrapper qa-case-section"><div className="content-header"><h2 className="h2">{data.title}</h2><div className="qa-scenario-heading-row"><p className="bodycopy qa-section-intro">{data.intro}</p><div className="qa-scenario-tabs" role="tablist" aria-label={data.title}>{data.items.map((item, index) => <button key={item.title} id={tabId(index)} type="button" role="tab" aria-label={item.title} aria-selected={activeIndex === index} aria-controls={panelId} tabIndex={activeIndex === index ? 0 : -1} onClick={() => setActiveIndex(index)} onKeyDown={event => handleTabKeyDown(event, index)}>{tabLabel(item, index)}</button>)}</div></div></div><div id={panelId} className="qa-scenario-table-region" role="tabpanel" aria-labelledby={tabId(activeIndex)} tabIndex="0"><table className="qa-scenario-table"><caption className="visually-hidden">{scenario.title}</caption><thead><tr><th colSpan="2" className="h3">{scenario.title}</th></tr></thead><tbody>{rows.map(([label, value]) => <tr key={label}><th scope="row" className="bodycopy">{label}</th><td>{value}</td></tr>)}</tbody></table></div></section>;
}

function flowPairs(fromCount, toCount) {
  if (fromCount === 1) return Array.from({ length: toCount }, (_, targetIndex) => [0, targetIndex]);
  if (toCount === 1) return Array.from({ length: fromCount }, (_, sourceIndex) => [sourceIndex, 0]);
  if (fromCount === toCount) return Array.from({ length: fromCount }, (_, itemIndex) => [itemIndex, itemIndex]);
  const edgeCount = Math.max(fromCount, toCount);
  return Array.from({ length: edgeCount }, (_, edgeIndex) => [Math.round((edgeIndex * (fromCount - 1)) / Math.max(edgeCount - 1, 1)), Math.round((edgeIndex * (toCount - 1)) / Math.max(edgeCount - 1, 1))]);
}

function connectorPath(source, target) {
  const distance = Math.max(target.x - source.x, 0);
  const handle = Math.max(distance * .42, 16);
  return `M ${source.x} ${source.y} C ${source.x + handle} ${source.y}, ${target.x - handle} ${target.y}, ${target.x} ${target.y}`;
}

function FlowLines({ flowRef, stages, slideIndex, active, incoming, outgoing }) {
  const [geometry, setGeometry] = useState({ width: 1, height: 1, internal: stages.slice(0, -1).map(() => []), incoming: [], outgoing: [] });
  const measure = useCallback(() => {
    const root = flowRef.current;
    if (!root || !active) return;
    const rootRect = root.getBoundingClientRect();
    if (rootRect.width <= 0 || rootRect.height <= 0) return;
    const nodeGroups = Array.from(root.children).filter(child => child.classList.contains("qa-flowchart-stage")).map(stage => Array.from(stage.querySelectorAll(".qa-flowchart-node")));
    const anchor = (node, side) => {
      const rect = node.getBoundingClientRect();
      return { x: (side === "left" ? rect.left : rect.right) - rootRect.left, y: rect.top + (rect.height / 2) - rootRect.top };
    };
    const internal = nodeGroups.slice(0, -1).map((sourceNodes, stageIndex) => flowPairs(sourceNodes.length, nodeGroups[stageIndex + 1].length).map(([sourceIndex, targetIndex]) => connectorPath(anchor(sourceNodes[sourceIndex], "right"), anchor(nodeGroups[stageIndex + 1][targetIndex], "left"))));
    const incomingPaths = !incoming ? [] : nodeGroups[0].map(node => {
      const target = anchor(node, "left");
      return `M 0 ${target.y} L ${target.x} ${target.y}`;
    });
    const lastNodes = nodeGroups[nodeGroups.length - 1] || [];
    const outgoingPaths = !outgoing ? [] : lastNodes.map(node => {
      const source = anchor(node, "right");
      return `M ${source.x} ${source.y} L ${rootRect.width} ${source.y}`;
    });
    setGeometry({ width: rootRect.width, height: rootRect.height, internal, incoming: incomingPaths, outgoing: outgoingPaths });
  }, [active, flowRef, incoming, outgoing]);

  useEffect(() => {
    if (!active) return undefined;
    measure();
    const frame = window.requestAnimationFrame ? window.requestAnimationFrame(measure) : null;
    const root = flowRef.current;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (root) observer?.observe(root);
    window.addEventListener("resize", measure);
    document.fonts?.ready?.then(measure);
    return () => {
      if (frame !== null && window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [active, flowRef, measure]);

  const markerId = `qa-flow-arrow-${slideIndex}`;
  return <svg className="qa-flowchart-lines" viewBox={`0 0 ${geometry.width} ${geometry.height}`} preserveAspectRatio="none" aria-hidden="true" focusable="false">
    <defs><marker id={markerId} markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto" markerUnits="strokeWidth"><path d="M0,0 L8,4 L0,8 Z" /></marker></defs>
    {incoming && <g className="qa-flowchart-edge qa-flowchart-edge-incoming is-connected" data-connection-count={stages[0].nodes.length}>{geometry.incoming.map((path, edgeIndex) => <path d={path} markerEnd={`url(#${markerId})`} key={`incoming-${edgeIndex}`} />)}</g>}
    {stages.slice(0, -1).map((stage, stageIndex) => <g className="qa-flowchart-connector" data-connection-count={flowPairs(stage.nodes.length, stages[stageIndex + 1].nodes.length).length} key={`internal-${stageIndex}`}>{(geometry.internal[stageIndex] || []).map((path, edgeIndex) => <path d={path} markerEnd={`url(#${markerId})`} key={`internal-${stageIndex}-${edgeIndex}`} />)}</g>)}
    {outgoing && <g className="qa-flowchart-edge qa-flowchart-edge-outgoing is-connected" data-connection-count={stages[stages.length - 1].nodes.length}>{geometry.outgoing.map((path, edgeIndex) => <path d={path} markerEnd={`url(#${markerId})`} key={`outgoing-${edgeIndex}`} />)}</g>}
  </svg>;
}

function FlowDiagram({ stages, slideIndex, active, flowLabel, incoming, outgoing }) {
  const flowRef = useRef(null);
  return <div ref={flowRef} className={`qa-flowchart${stages.length === 2 ? " is-two-stage" : ""}${incoming ? " has-incoming" : ""}${outgoing ? " has-outgoing" : ""}`} style={{ "--qa-stage-count": stages.length }} aria-label={flowLabel}>
    <FlowLines flowRef={flowRef} stages={stages} slideIndex={slideIndex} active={active} incoming={incoming} outgoing={outgoing} />
    {stages.map(stage => <section className="qa-flowchart-stage" key={stage.label}>
      <h3 className="bodycopy qa-flowchart-stage-label">{stage.label}</h3>
      <div className="qa-flowchart-stage-nodes" style={{ "--qa-node-count": stage.nodes.length }}>{stage.nodes.map(([title, text], nodeIndex) => <article className="qa-flowchart-node" key={title}><span className="qa-flowchart-node-index" aria-hidden="true">{String(nodeIndex + 1).padStart(2, "0")}</span><h4 className="h4">{title}</h4><p className="bodycopy">{text}</p></article>)}</div>
    </section>)}
  </div>;
}

export function Architecture({ data, locale }) {
  const [current, setCurrent] = useState(0);
  const viewportRef = useRef(null);
  const [scrollIndicator, setScrollIndicator] = useState({ visible: false, size: 100, offset: 0 });
  const flowLabel = locale === "de" ? "Systemfluss" : "System flow";
  const rawSlides = data.slides || [[0, 2], [1, 4], [3, 5]];
  const slideDefinitions = rawSlides.map((slide, index) => {
    const range = Array.isArray(slide) ? slide : slide.range;
    return {
      stages: data.stages.slice(range[0], range[1]),
      incoming: Array.isArray(slide) ? index > 0 : (slide.incoming ?? index > 0),
      outgoing: Array.isArray(slide) ? index < rawSlides.length - 1 : (slide.outgoing ?? index < rawSlides.length - 1)
    };
  });
  const slides = slideDefinitions.map(slide => slide.stages);
  const previous = () => setCurrent(index => index === 0 ? slides.length - 1 : index - 1);
  const next = () => setCurrent(index => index === slides.length - 1 ? 0 : index + 1);
  const slidePath = stages => stages.map(stage => stage.label).join(" → ");
  const updateScrollIndicator = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const maximum = Math.max(viewport.scrollHeight - viewport.clientHeight, 0);
    const size = viewport.scrollHeight > 0 ? Math.max((viewport.clientHeight / viewport.scrollHeight) * 100, 14) : 100;
    const offset = maximum > 0 ? (viewport.scrollTop / maximum) * (100 - size) : 0;
    setScrollIndicator({ visible: maximum > 2, size, offset });
  }, []);
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;
    viewport.scrollTop = 0;
    updateScrollIndicator();
    const frame = window.requestAnimationFrame ? window.requestAnimationFrame(updateScrollIndicator) : null;
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateScrollIndicator);
    observer?.observe(viewport);
    window.addEventListener("resize", updateScrollIndicator);
    return () => {
      if (frame !== null && window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", updateScrollIndicator);
    };
  }, [current, updateScrollIndicator]);
  return <section id="architecture" className="content-wrapper qa-case-section qa-workflow-section">
    <div className="content-header"><h2 id="architecture-heading" className="h2">{data.title}</h2>{data.intro && <p className="bodycopy qa-section-intro">{data.intro}</p>}</div>
    <div className="qa-workflow-slider" aria-roledescription="carousel" aria-label={data.title}>
      <div className="qa-workflow-toolbar"><p className="h3">{flowLabel}</p><div className="qa-workflow-controls"><button type="button" onClick={previous} aria-label={locale === "de" ? "Vorherige Systemfluss-Ansicht" : "Previous system-flow view"}><span className="material-symbols-outlined" aria-hidden="true">arrow_back</span></button><button type="button" onClick={next} aria-label={locale === "de" ? "Nächste Systemfluss-Ansicht" : "Next system-flow view"}><span className="material-symbols-outlined" aria-hidden="true">arrow_forward</span></button></div></div>
      <div className="qa-flowchart-scroll-shell">
        <div ref={viewportRef} className="qa-flowchart-viewport" role="region" aria-labelledby="architecture-heading" tabIndex="0" onScroll={updateScrollIndicator}>
          {slideDefinitions.map(({ stages, incoming, outgoing }, slideIndex) => <div className="qa-flowchart-panel" role="group" aria-roledescription="slide" aria-label={`${slideIndex + 1} / ${slides.length}`} hidden={current !== slideIndex} key={slidePath(stages)}>
            <FlowDiagram stages={stages} slideIndex={slideIndex} active={current === slideIndex} flowLabel={flowLabel} incoming={incoming} outgoing={outgoing} />
          </div>)}
        </div>
        <span className={`qa-flowchart-scroll-indicator${scrollIndicator.visible ? " is-visible" : ""}`} style={{ "--qa-scroll-size": `${scrollIndicator.size}%`, "--qa-scroll-offset": `${scrollIndicator.offset}%` }} aria-hidden="true"><span /></span>
      </div>
      <div className="qa-workflow-caption"><p className="bodycopy" aria-live="polite">{slidePath(slides[current])}</p><p><strong className="h4">{String(current + 1).padStart(2, "0")}</strong><span className="bodycopy"> / {String(slides.length).padStart(2, "0")}</span></p></div>
    </div>
    <SectionJump href="#contribution" locale={locale} />
  </section>;
}

export function Evidence({ data, media, sampleLabel, locale }) {
  return <section id="evidence" className="content-wrapper qa-case-section">
    <div className="content-header">
      <h2 className="h2">{data.title}</h2>
      {data.intro && <p className="bodycopy qa-section-intro">{data.intro}</p>}
    </div>
    <div className="artifact-wrapper">
      {data.items.map(([title, description, alt], index) => {
        const mediaItem = media.evidence[index];
        const localizedSource = value => typeof value === "string" ? value : (value?.[locale] || value?.de || value?.en || "");
        const source = typeof mediaItem === "string" ? mediaItem : localizedSource(mediaItem.src);
        const mobileSource = typeof mediaItem === "object" ? localizedSource(mediaItem.mobileSrc) : "";
        const isMockup = source.includes("-mock-");
        const isDiagram = typeof mediaItem === "object" && mediaItem.kind === "diagram";
        const image = <img
          src={source}
          alt={alt}
          loading="lazy"
          width={mediaItem.width || 1536}
          height={mediaItem.height || 1024}
        />;

        return <figure className={`teaser-uno qa-artifact${isMockup ? " qa-artifact--mockup" : ""}${isDiagram ? " qa-artifact--diagram" : ""}`} key={title}>
          <div className={`head${isMockup ? " qa-mockup-frame" : ""}${isDiagram ? " qa-diagram-frame" : ""}`}>
            {isDiagram && mobileSource
              ? <picture>
                  <source media="(max-width: 700px)" srcSet={mobileSource} />
                  {image}
                </picture>
              : image}
          </div>
          <figcaption className="body">
            <div className="inner-typo">
              <span className="qa-artifact-label bodycopy">{sampleLabel}</span>
              <h3 className="h4">{title}</h3>
              <p className="bodycopy">{description}</p>
            </div>
          </figcaption>
        </figure>;
      })}
    </div>
  </section>;
}

export function CaseActions({ data, locale, resumeUrl }) {
  if (!data) return null;
  return <section id="actions" className="content-wrapper qa-case-section qa-case-actions"><div className="content-header"><h2 className="h2">{data.title}</h2>{data.intro && <p className="bodycopy qa-section-intro">{data.intro}</p>}</div><div className="qa-cta-row"><a className="button-primary bodycopy" href={resumeUrl} target="_blank" rel="noreferrer">{data.primary}<span className="visually-hidden"> {locale === "de" ? "öffnet in einem neuen Tab" : "opens in a new tab"}</span></a><a className="button-secondary bodycopy" href={`/${locale}/contact`}>{data.secondary}</a></div></section>;
}
